import { CommonModule } from '@angular/common';
import { Component, computed, HostListener, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { CompanyService } from '../../../core/services/api-services/company.service';
import {
  CompanyEmployeeAdminView,
  CompanyEmployeesService
} from '../../../core/services/api-services/company-employees.service';
import { BreadcrumbComponent, BreadcrumbItem } from '../../../shared/components/breadcrumb/breadcrumb.component';
import { BreadcrumbService } from '../../../shared/components/breadcrumb/service/breadcrumb.service';
import { FilterPanelComponent } from '../../../shared/components/filter-panel/filter-panel.component';
import { FilterField, FilterResult } from '../../../shared/components/filter-panel/model/filter-field.model';
import { PageEvent, PaginatorComponent } from '../../../shared/components/paginator/paginator.component';

@Component({
  selector: 'app-company-employees',
  standalone: true,
  imports: [CommonModule, FormsModule, BreadcrumbComponent, PaginatorComponent, FilterPanelComponent],
  templateUrl: './company-employees.component.html',
  styleUrl: './company-employees.component.css'
})
export class CompanyEmployeesComponent implements OnInit, OnDestroy {

  breadcrumbItems: BreadcrumbItem[] = [];
  private destroy$ = new Subject<void>();
  private subs = new Subscription();
  private dataFetch$?: Subscription;

  // ── Table data ──────────────────────────────────────────────────────────
  employees: CompanyEmployeeAdminView[] = [];
  filteredEmployees: CompanyEmployeeAdminView[] = [];

  isLoading = false;
  error = '';

  // ── Pagination ───────────────────────────────────────────────────────────
  totalEmployees = 0;
  currentPage = 1;
  pageSize = 10;

  // ── Client-side search (searches current page) ───────────────────────────
  searchText = '';

  // ── Employer dropdown ────────────────────────────────────────────────────
  companies: any[] = [];
  filterCompanyId: number | '' = '';
  filterCompanySearchTerm = signal('');
  companyDropdownOpen = false;

  // ── Server-side filters (FilterPanel) ──────────────────────────────────
  filterEmployeeId = '';
  filterStatus = '';

  @ViewChild(FilterPanelComponent) filterPanel!: FilterPanelComponent;

  filterFields: FilterField[] = [
    {
      type: 'radio-group',
      controlName: 'filterType',
      label: 'Filter By:',
      defaultValue: 'EmployeeId',
      options: [
        {
          value: 'EmployeeId',
          label: 'Employee ID',
          field: {
            type: 'text',
            controlName: 'employeeId',
            placeholder: 'Enter Employee ID',
          },
        },
        {
          value: 'Status',
          label: 'Status',
          field: {
            type: 'select',
            controlName: 'status',
            placeholder: 'Select status',
            options: [
              { label: 'Active',     value: 'Active' },
              { label: 'Resigned',   value: 'Resigned' },
              { label: 'Terminated', value: 'Terminated' },
              { label: 'Suspended',  value: 'Suspended' },
            ],
          },
        },
      ],
    },
  ];

  get isServerFilterApplied(): boolean {
    return !!(this.filterCompanyId !== '' || this.filterEmployeeId || this.filterStatus);
  }

  // ── Company dropdown helpers ─────────────────────────────────────────────
  filteredCompanies = computed(() => {
    const term = this.filterCompanySearchTerm().toLowerCase().trim();
    if (!term) return this.companies;
    return this.companies.filter(c =>
      c.companyName.toLowerCase().includes(term) || String(c.companyId).includes(term)
    );
  });

  getSelectedCompanyName(): string {
    if (!this.filterCompanyId) return '';
    const found = this.companies.find(c => c.companyId === this.filterCompanyId);
    return found ? `${found.companyName} (${found.companyId})` : '';
  }

  openCompanyDropdown() {
    this.companyDropdownOpen = true;
    this.filterCompanySearchTerm.set('');
  }

  onFilterCompanySearchChange(event: Event) {
    const val = (event.target as HTMLInputElement).value;
    this.filterCompanySearchTerm.set(val);
    this.companyDropdownOpen = true;
  }

  selectCompany(company: any) {
    this.filterCompanyId = company ? company.companyId : '';
    this.filterCompanySearchTerm.set('');
    this.companyDropdownOpen = false;
    this.currentPage = 1;
    this.fetchEmployees();
  }

  onCompanyBlur() {
    setTimeout(() => {
      this.companyDropdownOpen = false;
      this.filterCompanySearchTerm.set('');
    }, 200);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.searchable-dropdown')) {
      this.companyDropdownOpen = false;
    }
  }

  constructor(
    private toaster: ToastrService,
    private companyService: CompanyService,
    private companyEmployeesService: CompanyEmployeesService,
    private breadcrumbService: BreadcrumbService
  ) {}

  ngOnInit(): void {
    this.breadcrumbService.breadcrumbs$
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => { this.breadcrumbItems = items; });

    this.breadcrumbService.setBreadcrumbs([
      { label: 'Dashboard', url: '/dashboard', icon: 'bi bi-house-door' },
      { label: 'Client', icon: 'bi bi-people' },
      { label: 'Company Employees', icon: 'bi bi-person-badge' }
    ]);

    this.loadCompanies();
    this.fetchEmployees();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.dataFetch$?.unsubscribe();
    this.subs.unsubscribe();
  }

  // ── Data loaders ─────────────────────────────────────────────────────────

  private loadCompanies(): void {
    this.subs.add(
      this.companyService.getExistingCompanies().subscribe({
        next: (res: any) => { this.companies = res?.success ? (res.data ?? []) : []; },
        error: ()        => { this.companies = []; }
      })
    );
  }

  fetchEmployees(): void {
    this.dataFetch$?.unsubscribe();
    this.isLoading = true;
    this.error = '';
    const companyId  = this.filterCompanyId !== '' ? this.filterCompanyId : null;
    const employeeId = this.filterEmployeeId.trim() || null;
    const status     = this.filterStatus || null;
    this.dataFetch$ = this.companyEmployeesService
      .getCompanyEmployeesAdminView(this.currentPage, this.pageSize, companyId, employeeId, status)
      .subscribe({
        next: res => {
          this.isLoading = false;
          if (res?.success && res.data) {
            this.employees      = res.data.employees    ?? [];
            this.totalEmployees = res.data.totalRecords ?? 0;
          } else {
            this.employees      = [];
            this.totalEmployees = 0;
          }
          this.applyClientSearch();
        },
        error: () => {
          this.isLoading         = false;
          this.error             = 'Failed to load employees. Please try again.';
          this.employees         = [];
          this.filteredEmployees = [];
          this.totalEmployees    = 0;
          this.toaster.error('Failed to load employees.');
        }
      });
  }

  // ── Client-side search (filters current page rows only) ──────────────────

  performSearch(): void {
    this.applyClientSearch();
  }

  private applyClientSearch(): void {
    const term = this.searchText.toLowerCase().trim();
    if (!term) {
      this.filteredEmployees = [...this.employees];
      return;
    }
    this.filteredEmployees = this.employees.filter(e =>
      (e.employeeId?.toLowerCase().includes(term)) ||
      (e.firstName?.toLowerCase().includes(term)) ||
      (e.lastName?.toLowerCase().includes(term)) ||
      (e.nic?.toLowerCase().includes(term)) ||
      (e.email?.toLowerCase().includes(term)) ||
      (e.mobile?.toLowerCase().includes(term)) ||
      (e.status?.toLowerCase().includes(term))
    );
  }

  // ── Filter panel callbacks ────────────────────────────────────────────────

  onFilter(result: FilterResult): void {
    this.filterEmployeeId = (result['employeeId'] as string) ?? '';
    this.filterStatus     = (result['status']     as string) ?? '';
    this.currentPage = 1;
    this.fetchEmployees();
  }

  onClear(): void {
    this.filterEmployeeId = '';
    this.filterStatus     = '';
    this.filterCompanyId  = '';
    this.searchText       = '';
    this.currentPage      = 1;
    this.filterPanel?.reset();
    this.fetchEmployees();
  }

  // ── Pagination ────────────────────────────────────────────────────────────

  onPageChange(event: PageEvent): void {
    this.currentPage = event.page;
    this.pageSize = event.pageSize;
    this.fetchEmployees();
  }

  // ── Badge helpers ─────────────────────────────────────────────────────────

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'Active':     return 'bg-success';
      case 'Resigned':   return 'bg-warning text-dark';
      case 'Terminated': return 'bg-danger';
      case 'Suspended':  return 'bg-secondary';
      default:           return 'bg-light text-dark';
    }
  }
}
